import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from "@mui/material";
import { useAuth } from "../../hooks/useAuth";
import { getPortfolioPromptIntakePrefill } from "../../api/client";
import { PROJECT_ARCHETYPES } from "../../data/portfolioKit";
import { L } from "../../utils/localized";
import type {
  IntakePrefill,
  PromptArchetype,
  PromptExperienceLevel,
  PromptIntake,
  PromptProject,
} from "../../api/client";

const STEP_KEYS = [
  "PromptStudioWizard.stepAboutYou",
  "PromptStudioWizard.stepYourAngle",
  "PromptStudioWizard.stepProjects",
  "PromptStudioWizard.stepReview",
];

const LEVELS: { value: PromptExperienceLevel; labelKey: string }[] = [
  { value: "junior", labelKey: "PromptStudioWizard.levelJunior" },
  { value: "mid", labelKey: "PromptStudioWizard.levelMid" },
  { value: "senior", labelKey: "PromptStudioWizard.levelSenior" },
  { value: "staff", labelKey: "PromptStudioWizard.levelStaffPlus" },
];

interface Props {
  generating: boolean;
  onSubmit: (intake: PromptIntake) => void;
}

/**
 * Outer component: fetches the intake prefill, then renders the form once the
 * query settles so the form's `useState` initializers run exactly once with the
 * data already available (no flicker, no effect that could clobber edits).
 * On error we render the form with an empty prefill — the wizard is never blocked.
 */
export default function PromptStudioWizard({ generating, onSubmit }: Props) {
  const { user } = useAuth();
  const { data, isLoading } = useQuery({
    queryKey: ["portfolioPromptIntakePrefill"],
    queryFn: getPortfolioPromptIntakePrefill,
    // Never fire for an unauthenticated user: a 401 here would trip the axios
    // interceptor and log the user out. Disabled query stays idle (not loading).
    enabled: Boolean(user),
    // Prefill is seeded once per wizard session; skip the global 30s refetch.
    staleTime: 5 * 60 * 1000,
  });

  if (isLoading) {
    return (
      <Paper sx={{ p: 4, borderRadius: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      </Paper>
    );
  }

  return (
    <WizardForm
      prefill={data ?? {}}
      generating={generating}
      onSubmit={onSubmit}
    />
  );
}

function WizardForm({
  prefill,
  generating,
  onSubmit,
}: Props & { prefill: IntakePrefill }) {
  const { user } = useAuth();
  const { t, i18n } = useTranslation("portfolio-kit");
  const archetypes = useMemo<{ value: PromptArchetype; label: string }[]>(
    () =>
      PROJECT_ARCHETYPES.map((a) => {
        const resolved = L(a, i18n.language);
        return { value: resolved.slug as PromptArchetype, label: resolved.title };
      }),
    [i18n.language],
  );
  const [activeStep, setActiveStep] = useState(0);

  const [name, setName] = useState(prefill.name ?? user?.display_name ?? "");
  const [targetRole, setTargetRole] = useState(prefill.target_role ?? "");
  const [archetype, setArchetype] = useState<PromptArchetype>("rag-agent");
  const [level, setLevel] = useState<PromptExperienceLevel>(
    prefill.experience_level ?? "senior",
  );
  const [positioning, setPositioning] = useState("");
  const [techStack, setTechStack] = useState(
    (prefill.tech_stack ?? []).join(", "),
  );
  const [tone, setTone] = useState("confident, technical");
  const [domain, setDomain] = useState("");
  const [email, setEmail] = useState(prefill.email ?? "");
  const [linkedin, setLinkedin] = useState("");
  const [github, setGithub] = useState("");
  const [projects, setProjects] = useState<PromptProject[]>([
    { title: "", summary: "", metrics: "" },
  ]);
  const [links, setLinks] = useState("");

  // Show the prefill cue only when a non-obvious field was populated.
  // Name/email alone are unsurprising, so they don't trigger it.
  const prefilledNonObvious = Boolean(
    prefill.target_role ||
      prefill.experience_level ||
      prefill.tech_stack?.length,
  );

  const techList = techStack
    .split(",")
    .map((tech) => tech.trim())
    .filter(Boolean);
  const linkList = links
    .split(/[\n,]/)
    .map((l) => l.trim())
    .filter(Boolean);

  const step0Valid = name.trim() && targetRole.trim() && domain.trim();
  const step1Valid = positioning.trim() && techList.length > 0 && tone.trim();
  const step2Valid = projects.some((p) => p.title.trim() && p.summary.trim());

  function updateProject(idx: number, patch: Partial<PromptProject>) {
    setProjects((prev) =>
      prev.map((p, i) => (i === idx ? { ...p, ...patch } : p))
    );
  }

  function handleSubmit() {
    onSubmit({
      name: name.trim(),
      target_role: targetRole.trim(),
      archetype,
      experience_level: level,
      positioning: positioning.trim(),
      tech_stack: techList,
      tone: tone.trim(),
      domain: domain.trim(),
      email: email.trim(),
      linkedin: linkedin.trim(),
      github: github.trim(),
      projects: projects
        .filter((p) => p.title.trim() && p.summary.trim())
        .map((p) => ({
          title: p.title.trim(),
          summary: p.summary.trim(),
          metrics: p.metrics.trim(),
        })),
      existing_links: linkList,
    });
  }

  return (
    <Paper sx={{ p: 4, borderRadius: 3 }}>
      {prefilledNonObvious && (
        <Alert severity="info" sx={{ mb: 3 }}>
          {t("PromptStudioWizard.prefillNotice")}
        </Alert>
      )}
      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {STEP_KEYS.map((stepKey) => (
          <Step key={stepKey}>
            <StepLabel>{t(stepKey)}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {activeStep === 0 && (
        <Stack spacing={2}>
          <TextField
            label={t("PromptStudioWizard.nameLabel")}
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            required
          />
          <TextField
            label={t("PromptStudioWizard.targetRoleLabel")}
            value={targetRole}
            onChange={(e) => setTargetRole(e.target.value)}
            fullWidth
            required
            placeholder={t("PromptStudioWizard.targetRolePlaceholder")}
          />
          <TextField
            label={t("PromptStudioWizard.domainLabel")}
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            fullWidth
            required
            placeholder={t("PromptStudioWizard.domainPlaceholder")}
          />
          <Select
            value={level}
            onChange={(e) => setLevel(e.target.value as PromptExperienceLevel)}
            fullWidth
          >
            {LEVELS.map((l) => (
              <MenuItem key={l.value} value={l.value}>
                {t(l.labelKey)}
              </MenuItem>
            ))}
          </Select>
          <Select
            value={archetype}
            onChange={(e) => setArchetype(e.target.value as PromptArchetype)}
            fullWidth
          >
            {archetypes.map((a) => (
              <MenuItem key={a.value} value={a.value}>
                {a.label}
              </MenuItem>
            ))}
          </Select>
          <TextField
            label={t("PromptStudioWizard.emailLabel")}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
            placeholder={t("PromptStudioWizard.emailPlaceholder")}
          />
          <TextField
            label={t("PromptStudioWizard.linkedinLabel")}
            value={linkedin}
            onChange={(e) => setLinkedin(e.target.value)}
            fullWidth
            placeholder={t("PromptStudioWizard.linkedinPlaceholder")}
          />
          <TextField
            label={t("PromptStudioWizard.githubLabel")}
            value={github}
            onChange={(e) => setGithub(e.target.value)}
            fullWidth
            placeholder={t("PromptStudioWizard.githubPlaceholder")}
          />
        </Stack>
      )}

      {activeStep === 1 && (
        <Stack spacing={2}>
          <TextField
            label={t("PromptStudioWizard.positioningLabel")}
            value={positioning}
            onChange={(e) => setPositioning(e.target.value)}
            fullWidth
            multiline
            rows={2}
            required
            placeholder={t("PromptStudioWizard.positioningPlaceholder")}
          />
          <TextField
            label={t("PromptStudioWizard.techStackLabel")}
            value={techStack}
            onChange={(e) => setTechStack(e.target.value)}
            fullWidth
            required
            placeholder={t("PromptStudioWizard.techStackPlaceholder")}
          />
          {techList.length > 0 && (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {techList.map((tech) => (
                <Chip key={tech} label={tech} size="small" />
              ))}
            </Stack>
          )}
          <TextField
            label={t("PromptStudioWizard.toneLabel")}
            value={tone}
            onChange={(e) => setTone(e.target.value)}
            fullWidth
            required
          />
          <TextField
            label={t("PromptStudioWizard.existingLinksLabel")}
            value={links}
            onChange={(e) => setLinks(e.target.value)}
            fullWidth
            multiline
            rows={2}
            placeholder={t("PromptStudioWizard.existingLinksPlaceholder")}
          />
        </Stack>
      )}

      {activeStep === 2 && (
        <Stack spacing={3}>
          {projects.map((p, idx) => (
            <Paper key={idx} variant="outlined" sx={{ p: 2 }}>
              <Stack spacing={1.5}>
                <TextField
                  label={t("PromptStudioWizard.projectTitleLabel", { index: idx + 1 })}
                  value={p.title}
                  onChange={(e) => updateProject(idx, { title: e.target.value })}
                  fullWidth
                />
                <TextField
                  label={t("PromptStudioWizard.summaryLabel")}
                  value={p.summary}
                  onChange={(e) =>
                    updateProject(idx, { summary: e.target.value })
                  }
                  fullWidth
                  multiline
                  rows={2}
                />
                <TextField
                  label={t("PromptStudioWizard.headlineMetricsLabel")}
                  value={p.metrics}
                  onChange={(e) =>
                    updateProject(idx, { metrics: e.target.value })
                  }
                  fullWidth
                  placeholder={t("PromptStudioWizard.headlineMetricsPlaceholder")}
                />
              </Stack>
            </Paper>
          ))}
          <Button
            onClick={() =>
              setProjects((p) => [...p, { title: "", summary: "", metrics: "" }])
            }
            disabled={projects.length >= 10}
          >
            {t("PromptStudioWizard.addAnotherProject")}
          </Button>
        </Stack>
      )}

      {activeStep === 3 && (
        <Box>
          <Typography variant="h6" mb={1}>
            {t("PromptStudioWizard.reviewHeading")}
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            {t("PromptStudioWizard.reviewSummary", {
              name,
              targetRole,
              archetype,
              domain,
              count: projects.filter((p) => p.title.trim()).length,
            })}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("PromptStudioWizard.generatingHint")}
          </Typography>
        </Box>
      )}

      <Stack direction="row" justifyContent="space-between" mt={4}>
        <Button
          disabled={activeStep === 0 || generating}
          onClick={() => setActiveStep((s) => s - 1)}
        >
          {t("PromptStudioWizard.backButton")}
        </Button>
        {activeStep < STEP_KEYS.length - 1 ? (
          <Button
            variant="contained"
            disabled={
              (activeStep === 0 && !step0Valid) ||
              (activeStep === 1 && !step1Valid) ||
              (activeStep === 2 && !step2Valid)
            }
            onClick={() => setActiveStep((s) => s + 1)}
          >
            {t("PromptStudioWizard.nextButton")}
          </Button>
        ) : (
          <Button variant="contained" disabled={generating} onClick={handleSubmit}>
            {generating
              ? t("PromptStudioWizard.generatingButton")
              : t("PromptStudioWizard.generateButton")}
          </Button>
        )}
      </Stack>
    </Paper>
  );
}
